import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

import {
    TextField, Button, Alert, CircularProgress, InputAdornment,
    Typography, Box, IconButton, Collapse, Tooltip,
} from '@mui/material';

import {
    Visibility, VisibilityOff,
    CheckCircleOutline,
    KeyboardCapslock as CapsIcon,
    LocalHospital as Hospital,
    Person as User,
    Lock,
    ShieldOutlined as ShieldIcon,
} from '@mui/icons-material';

import { companyInfo, URL } from './BaseURL';
import FirstLoginDialog from './FirstLoginDialog ';
import BrandAside from './Login/BrandAside';
import SopLibraryDialog from './Login/SopLibraryDialog';
import { socket } from './services/socket';
import { BRAND, PRODUCT, FONT_STACK } from '../brand';

// ═════════════════════════════════════════════════════════════════════════════
//  SIGN IN — MEFIMS
// ═════════════════════════════════════════════════════════════════════════════
//
//  Full-bleed, edge to edge. Two zones that between them cover the whole
//  viewport with no canvas showing through and nothing floating:
//
//    left   a plain white credential panel — the reason anyone opens this
//           screen, so it gets the first read on a left-to-right scan
//    right  the navy information panel (BrandAside): product identity, the
//           clinical-instrument motif, and the compliance footing
//
//  The Policy & SOP library that used to live here has been removed. A
//  sign-in screen has one job, and reading procedures is not it — the library
//  belongs behind authentication, next to the work it governs.
//
//  Authentication behaviour is unchanged from every previous revision:
//  licence gating, branch blocking, socket registration, session resume, and
//  the forced first-login password change all work exactly as before.

const NAVY        = BRAND.navy;
const NAVY_DARK   = BRAND.navyDark;
const ACCENT      = BRAND.green;
const ACCENT_DARK = BRAND.greenDark;
const ACCENT_LT   = BRAND.greenLight;

// Hospitals run around the clock; greet by shift rather than assuming daytime.
function greetingFor(date) {
    const h = date.getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
}

function Login() {
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState(false);
    const [companylogo,  setcompanylogo]  = useState('');
    const [companyname,  setcompanyName]  = useState('');
    const [company,      setcompany]      = useState([]);
    const [loading,      setLoading]      = useState(false);
    const [mounted,      setMounted]      = useState(false);
    const [formData,     setFormData]     = useState({ username: '', password: '' });
    const [usernameError, setusernameError] = useState(false);
    const [passwordError, setpasswordError] = useState(false);
    const [error,         setError]         = useState('');
    const [success,       setSuccess]       = useState(false);
    const [capsOn,        setCapsOn]        = useState(false);
    const [now,           setNow]           = useState(new Date());

    const [showFirstLogin,  setShowFirstLogin]  = useState(false);
    const [firstLoginEmail, setFirstLoginEmail] = useState('');
    const [pendingUserData, setPendingUserData] = useState(null);

    // The Policy & SOP library. Opened on demand from the information panel —
    // the dialog fetches its own contents the first time it is shown, so the
    // sign-in screen costs nothing extra to load for the people who never
    // open it.
    const [libraryOpen, setLibraryOpen] = useState(false);

    useEffect(() => { setMounted(true); }, []);

    // Ticks every 30s: only HH:mm is shown, so a per-second interval would
    // re-render 30x more often for no visible change.
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 30_000);
        return () => clearInterval(t);
    }, []);

    // ── company info ───────────────────────────────────────────────────────
    const loadCompany = async () => {
        try {
            const { data } = await axios.get(companyInfo);
            if (data.success) setcompany(data.results || []);
        } catch (err) {
            console.error('loadCompany:', err);
        }
    };
    useEffect(() => { loadCompany(); }, []);
    useEffect(() => {
        if (Array.isArray(company) && company.length > 0) {
            setcompanyName(company[0].name);
            setcompanylogo(company[0].logo);
            localStorage.setItem('companyInfo', JSON.stringify(company[0]));
        }
    }, [company]);

    // ── login submit ───────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const { data } = await axios.post(URL + 'login', {
                username: formData.username,
                password: formData.password,
            });

            if (data.success) {
                const user = data.user;

                if (user.first_login) {
                    setFirstLoginEmail(user.email);
                    setPendingUserData(user);
                    setShowFirstLogin(true);
                    setLoading(false);
                    return;
                }

                // ── LICENSE EXPIRY WARNING (≤ 90 days) — login still proceeds ──
                if (data.licenseWarning && data.daysLeft) {
                    const d = data.daysLeft;
                    const urgencyColor = d <= 7 ? '#dc2626' : d <= 30 ? '#ea580c' : '#d97706';

                    await Swal.fire({
                        icon:  d <= 30 ? 'error' : 'warning',
                        title: d <= 7 ? '⚠️ License Expiring Very Soon!' : '📅 License Expiry Notice',
                        html: `
                            <div style="text-align:center; padding:8px 0">
                                <div style="
                                    display:inline-block;
                                    background:${urgencyColor};
                                    color:#fff;
                                    font-size:40px;
                                    font-weight:900;
                                    padding:12px 28px;
                                    border-radius:12px;
                                    margin-bottom:12px;
                                ">${d}</div>
                                <p style="color:#374151; font-size:15px; font-weight:600; margin:0 0 6px">
                                    day${d === 1 ? '' : 's'} remaining
                                </p>
                                <p style="color:#6b7280; font-size:13px; margin:0 0 14px">
                                    Your ${PRODUCT.name} system license will expire soon.
                                </p>
                                <div style="
                                    background:#fef3c7;
                                    border:1px solid #fbbf24;
                                    border-radius:10px;
                                    padding:10px 14px;
                                    text-align:left;
                                    font-size:13px;
                                    color:#92400e;
                                ">
                                    <strong>Action required:</strong> Contact ${PRODUCT.vendor} support
                                    to renew before expiry to avoid service interruption.
                                </div>
                            </div>
                        `,
                        confirmButtonText:  'Continue to Dashboard',
                        confirmButtonColor: urgencyColor,
                        allowOutsideClick:  false,
                    });
                }

                completeLogin(user);
            }

        } catch (err) {
            setLoading(false);

            const data   = err.response?.data;
            const status = err.response?.status;

            if (data?.branchBlocked) {
                await Swal.fire({
                    icon:  'warning',
                    title: 'Branch not active',
                    html: `
                        <div style="text-align:center; padding:10px 0">
                            <p style="color:#64748b; font-size:14px; margin-bottom:8px">
                                ${data?.message || 'Your branch is not currently active.'}
                            </p>
                            <p style="color:#94a3b8; font-size:12px">
                                A System Administrator must approve / reactivate the branch before you can log in.
                            </p>
                        </div>
                    `,
                    confirmButtonText:  'OK',
                    confirmButtonColor: '#b45309',
                });
                return;
            }

            if (status === 403 || data?.licenseBlocked) {
                await Swal.fire({
                    icon:  'error',
                    title: '🔒 System License Expired',
                    html: `
                        <div style="text-align:center; padding:10px 0">
                            <p style="color:#64748b; font-size:14px; margin-bottom:8px">
                                ${data?.message || 'Your license has expired or been suspended.'}
                            </p>
                            <p style="color:#94a3b8; font-size:12px">
                                Please contact ${PRODUCT.vendor} support to renew your license.
                            </p>
                        </div>
                    `,
                    confirmButtonText:  'OK',
                    confirmButtonColor: '#d33',
                    allowOutsideClick:  false,
                    allowEscapeKey:     false,
                });
                return;
            }

            setError(data?.message || 'Invalid username or password');
        }
    };

    const completeLogin = (user) => {
        setSuccess(true);

        const userInfo = {
            name:       user.name,
            email:      user.email,
            department: user.department,
            role:       user.role,
            staff_id:   user.staff_id,
            is_staff:   user.is_staff,
            trans_code: user.id,
            username:   user.username,
            created_at: user.created_at,
            branch:     user.branch || null,   // { id, code, name, location, isMain }
        };

        localStorage.setItem('userInfo', JSON.stringify(userInfo));

        // ── Default license states until server responds via socket ──────────
        localStorage.setItem('license_ai',        JSON.stringify({ allowed: true }));
        localStorage.setItem('license_call_chat', JSON.stringify({ allowed: true }));

        try {
            if (!socket.connected) {
                socket.connect();
            }

            const registerUser = () => {
                socket.emit('register-user', { userId: String(user.id) });
            };

            if (socket.connected) {
                registerUser();
            } else {
                socket.once('connect', registerUser);
            }

            // ── Listen to license events sent back by the server ─────────────
            socket.off('ai-license');
            socket.off('call-chat-license');

            socket.on('ai-license', (data) => {
                console.log('[LICENSE] ai-license received:', data);
                localStorage.setItem('license_ai', JSON.stringify(data));
            });

            socket.on('call-chat-license', (data) => {
                console.log('[LICENSE] call-chat-license received:', data);
                localStorage.setItem('license_call_chat', JSON.stringify(data));
            });

        } catch (err) {
            console.error('Socket registration error:', err);
        }

        // ── Resume previous panel if auto-logged-out recently ───────────────
        const RESUME_WINDOW_MS = 60 * 60 * 1000;
        let resumeRoute = '/dashboard';
        try {
            const raw = localStorage.getItem('lastSession');
            if (raw) {
                const last = JSON.parse(raw);
                const sameUser     = last?.username === user.username;
                const sameDay      = last?.logoutDate === new Date().toDateString();
                const withinWindow = Date.now() - (last?.logoutTime || 0) < RESUME_WINDOW_MS;
                const validRoute   = typeof last?.route === 'string'
                    && last.route.startsWith('/dashboard/')
                    && last.route !== '/dashboard/';
                if (sameUser && sameDay && withinWindow && validRoute) {
                    resumeRoute = last.route;
                }
            }
        } catch (e) {
            // ignore — fall back to default
        }
        localStorage.removeItem('lastSession');

        setTimeout(() => navigate(resumeRoute), 1000);
    };

    useEffect(() => () => { socket.off('connect'); }, []);

    const usernameErrorhandel = () => setusernameError(!formData.username);
    const passwordErrorhandel = () => setpasswordError(!formData.password);

    // Caps Lock silently defeats a correct password and is the commonest cause
    // of "the system won't let me in" on shared ward terminals.
    const trackCaps = (e) => {
        if (typeof e.getModifierState === 'function') {
            setCapsOn(e.getModifierState('CapsLock'));
        }
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            background: '#fff',
            fontSize: 15,
            transition: 'box-shadow .16s ease, border-color .16s ease',
            '& fieldset':             { borderColor: BRAND.line },
            '&:hover fieldset':       { borderColor: NAVY },
            '&.Mui-focused fieldset': { borderColor: NAVY, borderWidth: '1.5px' },
            '&.Mui-focused':          { boxShadow: `0 0 0 4px ${NAVY}14` },
        },
        '& .MuiOutlinedInput-input': { py: 1.85 },
        '& .MuiInputLabel-root.Mui-focused': { color: NAVY, fontWeight: 600 },
    };

    // ══════════════════════════════════════════════════════════════════════
    return (
        <Box
            sx={{
                minHeight: '100vh',
                '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
                display: 'grid',
                // Edge to edge. No page padding, no rounded container, no
                // background showing around a floating card — the two zones
                // ARE the page. Form column first: it is what the user came
                // for, and it stays first when the grid collapses on mobile.
                //
                // Exactly 1fr 1fr: the seam lands on the centre line, so the
                // two halves are the same width at every viewport size.
                gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                fontFamily: FONT_STACK,
                background: '#fff',
            }}
        >
            {/* The panel animations now live inside BrandAside, alongside the
                markup that uses them — nothing on this side is animated. */}

            {/* ══════════════════════════════════════════════════════════════
                LEFT — credential panel
               ══════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    background: '#fff',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    px: { xs: 3, sm: 5, lg: 6, xl: 8 },
                    py: { xs: 4.5, lg: 6 },
                    position: 'relative',
                }}
            >
                {/* Green hairline down the seam. The form sits on the LEFT, so
                    the seam is this panel's right edge. */}
                <Box aria-hidden sx={{ display: { xs: 'none', lg: 'block' }, position: 'absolute', right: 0, top: 0, bottom: 0, width: 3, background: `linear-gradient(180deg, ${ACCENT_LT}, ${ACCENT}, transparent)`, zIndex: 2 }} />

                <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto', opacity: mounted ? 1 : 0, transition: 'opacity .4s ease' }}>

                    {/* ── the facility this install belongs to ── */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 4 }}>
                        <Box
                            sx={{
                                width: 52, height: 52, borderRadius: '14px', flexShrink: 0,
                                display: 'grid', placeItems: 'center', overflow: 'hidden',
                                background: companylogo ? '#fff' : `linear-gradient(135deg, ${BRAND.navyLight} 0%, ${NAVY} 100%)`,
                                border: companylogo ? `1px solid ${BRAND.line}` : 'none',
                                boxShadow: companylogo ? '0 2px 10px rgba(15,31,61,.08)' : `0 6px 18px ${NAVY}2E`,
                                p: companylogo ? .875 : 0,
                            }}
                        >
                            {companylogo
                                ? <Box component="img" src={companylogo} alt="" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                : <Hospital sx={{ fontSize: 26, color: '#fff' }} />}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: BRAND.inkFaint, letterSpacing: '1.3px', textTransform: 'uppercase' }}>
                                Licensed installation
                            </Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: BRAND.ink, lineHeight: 1.3, letterSpacing: '-.2px' }} noWrap>
                                {companyname || 'Awaiting facility registration'}
                            </Typography>
                        </Box>
                    </Box>

                    {/* ── heading ── */}
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: ACCENT, textTransform: 'uppercase', letterSpacing: '1.4px' }}>
                        {greetingFor(now)}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: 27, lg: 31 }, fontWeight: 800, color: BRAND.ink, letterSpacing: '-.8px', mt: .625 }}>
                        Sign in
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: BRAND.inkMuted, mt: 1, mb: 3.5 }}>
                        Enter your credentials to access the clinical workspace.
                    </Typography>

                    {success ? (
                        <Alert icon={<CheckCircleOutline fontSize="inherit" />} severity="success" sx={{ mb: 2.5, borderRadius: '12px' }}>
                            <strong>Signed in.</strong> Opening your workspace…
                        </Alert>
                    ) : error ? (
                        <Alert severity="error" sx={{ mb: 2.5, borderRadius: '12px' }}>{error}</Alert>
                    ) : null}

                    <form onSubmit={handleSubmit} noValidate>
                        <TextField
                            label="Username" variant="outlined" fullWidth
                            autoComplete="username" autoFocus
                            value={formData.username}
                            onChange={e => setFormData({ ...formData, username: e.target.value })}
                            required error={usernameError} onBlur={usernameErrorhandel}
                            sx={{ ...fieldSx, mb: 2.25 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <User sx={{ color: NAVY, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            label="Password" variant="outlined" fullWidth
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            error={passwordError} onBlur={passwordErrorhandel}
                            value={formData.password}
                            onChange={e => setFormData({ ...formData, password: e.target.value })}
                            onKeyUp={trackCaps} onKeyDown={trackCaps}
                            sx={fieldSx}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: NAVY, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <IconButton size="small" onClick={() => setShowPassword(p => !p)}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        sx={{ color: BRAND.inkFaint, '&:hover': { color: NAVY, backgroundColor: `${NAVY}0F` } }}>
                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                ),
                            }}
                        />

                        <Collapse in={capsOn}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: .875, mt: 1.25, px: 1.375, py: .875, borderRadius: '10px', background: '#FFF8EC', border: `1px solid ${BRAND.warning}44` }}>
                                <CapsIcon sx={{ fontSize: 16, color: BRAND.warning }} />
                                <Typography sx={{ fontSize: 12, color: '#8A5A12', fontWeight: 600 }}>Caps Lock is on</Typography>
                            </Box>
                        </Collapse>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                            <Tooltip title="Contact your System Administrator to reset your password" arrow>
                                <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: NAVY, cursor: 'pointer', '&:hover': { color: NAVY_DARK, textDecoration: 'underline' } }}>
                                    Forgot password?
                                </Box>
                            </Tooltip>
                        </Box>

                        <Button
                            variant="contained" fullWidth type="submit" disabled={loading}
                            sx={{
                                mt: 3, borderRadius: '12px', py: 1.6, fontSize: 15.5, fontWeight: 700,
                                textTransform: 'none', letterSpacing: '.2px',
                                background: `linear-gradient(135deg, ${ACCENT_LT} 0%, ${ACCENT} 55%, ${ACCENT_DARK} 100%)`,
                                boxShadow: `0 6px 18px ${ACCENT}47`,
                                '&:hover':    { filter: 'brightness(1.06)', boxShadow: `0 8px 26px ${ACCENT}5C` },
                                '&:disabled': { background: '#C7D2E0', color: '#fff', boxShadow: 'none' },
                                transition: 'all .22s ease',
                            }}
                        >
                            {loading
                                ? <><CircularProgress size={19} color="inherit" sx={{ mr: 1.25 }} />Signing in…</>
                                : <>Sign In <Box component="span" sx={{ ml: 1 }}>→</Box></>}
                        </Button>
                    </form>

                    {/* ── footing ── */}
                    <Box sx={{ mt: 4.5, pt: 2.75, borderTop: `1px solid ${BRAND.line}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mb: 2 }}>
                            <ShieldIcon sx={{ fontSize: 14, color: BRAND.inkFaint }} />
                            <Typography sx={{ fontSize: 11.5, color: BRAND.inkFaint }}>
                                All access to this system is logged and audited.
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                            <Box>
                                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: BRAND.inkFaint, letterSpacing: '1.3px' }}>
                                    POWERED BY
                                </Typography>
                                <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY, letterSpacing: '.4px', mt: .25 }}>
                                    {PRODUCT.vendor}
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: 11.5, color: BRAND.inkFaint, fontVariantNumeric: 'tabular-nums' }}>
                                {now.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}
                                {'  ·  '}
                                {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* ══════════════════════════════════════════════════════════════
                RIGHT — information panel
                Second in DOM order, so it also falls BELOW the form when the
                grid collapses to one column on phones and tablets.
               ══════════════════════════════════════════════════════════════ */}
            <BrandAside onOpenLibrary={() => setLibraryOpen(true)} />

            {/* Read-only policy browser. Reachable before sign-in by design —
                staff need the procedure before they have a session. */}
            <SopLibraryDialog open={libraryOpen} onClose={() => setLibraryOpen(false)} />

            {/* Mandatory on a first sign-in. The username is passed through
                read-only — it is fixed at account creation and this dialog
                offers no way to change it. `currentPassword` is the one they
                just authenticated with, so the server can re-verify identity
                before accepting a new password. */}
            <FirstLoginDialog
                open={showFirstLogin}
                username={pendingUserData?.username}
                email={firstLoginEmail}
                trans_code={pendingUserData?.id}
                currentPassword={formData.password}
                onCancel={() => {
                    setShowFirstLogin(false);
                    setPendingUserData(null);
                    setFormData({ username: formData.username, password: '' });
                    setLoading(false);
                }}
                onSuccess={(fixedUsername) => {
                    setShowFirstLogin(false);
                    setPendingUserData(null);
                    Swal.fire({
                        title: 'Password updated',
                        text: 'Sign in again with your new password.',
                        icon: 'success',
                        confirmButtonColor: ACCENT,
                    });
                    setFormData({ username: fixedUsername || formData.username, password: '' });
                    setLoading(false);
                }}
            />
        </Box>
    );
}

export default Login;
